import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSessionNoteChangeHandler from '@hooks/useSessionNoteChangeHandler';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import NoteEditor from '@components/Clinicians/Session/Components/NoteEditor';
import { Accordion } from '@components/Clinicians/Accordion';
import { confirmationModal } from 'vl-common/src/components/Modals';
import { isBlank } from '@helpers/htmlUtils';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';

const AdditionalNotesFormItem = () => {
  const { t } = useTranslation('common');

  const {
    appointment: { read_only: readOnly, appointment_completed: isCompleted }
  } = useSessionAppointment();

  const appointment_id = useSessionAppointmentId();
  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);

  const isV2Appointment = detailsCurrentAppointment?.appt_version === 2;

  const handleNoteChange = useSessionNoteChangeHandler({ field: 'D-COM' });

  const [fieldValue, setFieldValue] = useSessionFormState('D-COM');
  const [isCheckedManually, setIsCheckedManually] = useState(false);

  const isEmptyFieldValue = Boolean(isBlank(fieldValue));

  const handleToggleAccordion = (checked: boolean) => {
    if (checked) {
      setIsCheckedManually(true);
      return;
    }

    if (isEmptyFieldValue) {
      setIsCheckedManually(false);
      return;
    }

    confirmationModal({
      title: 'Are you sure?',
      content: 'Your additional comments will be deleted',
      closable: true,
      onOk: () => {
        handleNoteChange({ field: 'D-COM', value: '' });
        setFieldValue(undefined);
        setIsCheckedManually(false);
      }
    });
  };

  const isExpanded = isCheckedManually || !isEmptyFieldValue;

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('D-COM');

  return (
    <Accordion
      isCollapsibleByCheckbox={isV2Appointment}
      key="dcom-form-item"
      field="dcom"
      disabled={isCompleted || readOnly}
      label={getNoteLabel('D-COM')}
      required={isRequired}
      {...(isV2Appointment && { expanded: isExpanded, onChange: handleToggleAccordion })}
    >
      <NoteEditor
        data-testid="clinical-dcom"
        field="D-COM"
        ariaLabel={getNoteLabel('D-COM')}
        placeholder={t('clinicalDCOMPlaceholder')}
        readOnly={readOnly || isCompleted}
        onChange={handleNoteChange}
      />
    </Accordion>
  );
};

export default AdditionalNotesFormItem;
